import React, { useRef, useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import ImmediateOnDemandWaiting from "../../common/immediateOnDemand/ImmediateOnDemandWaiting";
import ImmediateOnDemandThankYou from "../../common/immediateOnDemand/ImmediateOnDemandThankYou";
import { getAppointmentId } from "../../common/immediateOnDemand/matchingUtils";
import immediateOnDemandRealtime from "../../../services/immediateOnDemandRealtime";
import useStoppableSound from "../../common/immediateOnDemand/useStoppableSound";
import "../../common/immediateOnDemand/immediateOnDemand.css";
import { useTranslation } from "react-i18next";

// value is what gets used/sent; label stays English and labelKey is translated for display.
const WAIT_OPTIONS = [
  { label: "15 Minutes", labelKey: "fifteenMinutes", value: "15" },
  { label: "30 Minutes", labelKey: "thirtyMinutes", value: "30" },
];

// Fallback message kept in state in English; translated only when shown.
const ELIGIBILITY_ERROR = "Unable to check on-demand availability. Please try again.";

export default function MediumImmediateOnDemand() {
  const { t } = useTranslation("mediumMisc");
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const waitOptionLabel = (option) =>
    t(`immediateOnDemand.waitOptions.${option.labelKey}`, { defaultValue: option.label });
  const displayWaitOptions = WAIT_OPTIONS.map((option) => ({
    ...option,
    label: waitOptionLabel(option),
  }));
  const displayEligibilityMessage = (message) =>
    message === ELIGIBILITY_ERROR ? t("immediateOnDemand.eligibilityError") : message;
  const audioRef = useRef(null);
  const [step, setStep] = useState("settings");
  const [signedIntoZoom, setSignedIntoZoom] = useState(false);
  const [waitTime, setWaitTime] = useState("");
  const [customMinutes, setCustomMinutes] = useState("");
  const [showCustomInput, setShowCustomInput] = useState(false);
  const [matchData, setMatchData] = useState(null);
  const [eligibility, setEligibility] = useState({
    checked: false,
    eligible: false,
    message: "",
  });
  const source = searchParams.get("source");
  const sourceAppointmentId = searchParams.get("sourceAppointmentId");
  const isBackfill = source === "backfill" && sourceAppointmentId;

  const [isTestSoundPlaying, setIsTestSoundPlaying] = useState(false);

  const stopTestSound = () => {
    const audio = audioRef.current;
    if (audio) {
      audio.pause();
      audio.currentTime = 0;
    }
    setIsTestSoundPlaying(false);
  };

  // Toggle: first click plays the chime, second click stops it.
  const toggleTestSound = () => {
    const audio = audioRef.current;
    if (!audio) return;

    if (isTestSoundPlaying) {
      stopTestSound();
      return;
    }

    audio.currentTime = 0;
    audio.volume = 0.25;
    audio
      .play()
      .then(() => setIsTestSoundPlaying(true))
      .catch(() => setIsTestSoundPlaying(false));
  };

  // Reset the button when the chime finishes on its own, and stop the
  // test sound if the user leaves the settings screen (e.g. Go On-Demand).
  useEffect(() => {
    if (step !== "settings") return undefined;
    const audio = audioRef.current;
    if (!audio) return undefined;

    const handleEnded = () => setIsTestSoundPlaying(false);
    audio.addEventListener("ended", handleEnded);

    return () => {
      audio.removeEventListener("ended", handleEnded);
      audio.pause();
      audio.currentTime = 0;
      setIsTestSoundPlaying(false);
    };
  }, [step]);
 

  const isBlockedByUpcomingAppointment =
    eligibility.checked && eligibility.eligible === false;

  useEffect(() => {
    let isMounted = true;

    immediateOnDemandRealtime
      .getEligibility({
        ...(isBackfill ? { source: "backfill", sourceAppointmentId } : {}),
      })
      .then((response) => {
        if (!isMounted) return;

        setEligibility({
          checked: true,
          eligible:
            response &&
            response.eligible === true &&
            response.hasAppointmentWithinThirtyMinutes !== true,
          message: response && response.message ? response.message : "",
        });
      })
      .catch(() => {
        if (!isMounted) return;

        setEligibility({
          checked: true,
          eligible: false,
          message: ELIGIBILITY_ERROR,
        });
      });

    return () => {
      isMounted = false;
    };
  }, [isBackfill, sourceAppointmentId]);

  const goToWaitingRoom = () => {
    if (!eligibility.checked || isBlockedByUpcomingAppointment) return;

    setStep("waiting");
  };
  const isCustomWaitTime = waitTime && waitTime !== "15" && waitTime !== "30";

  const handleCustomMinutesChange = (e) => {
    const raw = e.target.value.replace(/[^0-9]/g, "");
    if (raw === "") {
      setCustomMinutes("");
      setWaitTime("");
      return;
    }
    const num = parseInt(raw, 10);
    if (num >= 1 && num <= 60) {
      setCustomMinutes(String(num));
      setWaitTime(String(num));
    }
  };

  const storeImmediateAppointmentId = (data) => {
    const appointmentId = getAppointmentId(data);
    if (appointmentId) {
      localStorage.setItem("immediate_session_appointment_id", appointmentId);
    }
  };
  useEffect(() => {
    console.log("Step changed to:", step);
  }, [step]);

  // Confirmation sound (ringNew.wav, ~2 min): stops on any click, key press,
  // hover over a button/link, tab switch, or leaving the screen.
  useStoppableSound(audioRef, step === "thankyou");

  if (step === "waiting") {
    return (<>
       <audio ref={audioRef} src="/ring/ringNew.wav" preload="auto" />
      <ImmediateOnDemandWaiting
        participantRole="provider"
        settings={{ waitTime, signedIntoZoom, waitOptions: displayWaitOptions }}
        queueSource={isBackfill ? "backfill" : undefined}
        sourceAppointmentId={isBackfill ? sourceAppointmentId : undefined}
        exitPath="/service-entry"
        onMatched={(data) => {
          setMatchData(data);
          storeImmediateAppointmentId(data);
          setStep("thankyou");
        }}
        title={t("immediateOnDemand.waitingTitle")}
        matchedPath={(matchData) => {
          const appointmentId = matchData && matchData.appointmentId;
          return appointmentId
            ? `/thankyou?from=goondemand&appointment_id=${appointmentId}`
            : "/thankyou?from=goondemand";
        }}
      />
 
    </>);
  }

  if (step === "thankyou") {
    return (
      <>
      <audio ref={audioRef} src="/ring/ringNew.wav" preload="auto" />
      <ImmediateOnDemandThankYou
        matchData={matchData}
        participantRole="provider"
        onDone={() => navigate("/appointments")}
      />
      </>
    );
  }


  return (
    <div className="immediate-ondemand-page">
      <audio ref={audioRef} src="/ring/ringNew.wav" preload="auto" />

      <div className="immediate-ondemand-card iod-v2-card">
        {/* Header */}
        <div className="iod-v2-header">
          <div className="iod-v2-header-icon">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
            </svg>
          </div>
          <div>
            <h2 className="iod-v2-title">{isBackfill ? t("immediateOnDemand.titleBackfill") : t("immediateOnDemand.titleClient")}</h2>
            <p className="iod-v2-subtitle">{t("immediateOnDemand.subtitle")}</p>
          </div>
        </div>

        {/* Zoom row */}
        <div
          className={`iod-v2-row${signedIntoZoom ? " iod-v2-row--checked" : ""}`}
          onClick={() => setSignedIntoZoom(!signedIntoZoom)}
          role="checkbox"
          aria-checked={signedIntoZoom}
          tabIndex={0}
          onKeyDown={(e) => e.key === " " && setSignedIntoZoom(!signedIntoZoom)}
        >
          <div className={`iod-v2-row-icon${signedIntoZoom ? " iod-v2-row-icon--active" : ""}`}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10" />
              {signedIntoZoom && <polyline points="8 12 11 15 16 9" />}
            </svg>
          </div>
          <div>
            <p className="iod-v2-row-title">{t("immediateOnDemand.signInZoom")}</p>
            <p className="iod-v2-row-sub">{t("immediateOnDemand.readyToConnect")}</p>
          </div>
        </div>

        {/* Wait time row */}
        <div className="iod-v2-row iod-v2-row--static">
          <div className="iod-v2-row-icon">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10" />
              <polyline points="12 6 12 12 16 14" />
            </svg>
          </div>
          <div className="iod-v2-row-body">
            <p className="iod-v2-row-title">{t("immediateOnDemand.setWaitTime")}</p>
            <div className="iod-v2-pills">
              {WAIT_OPTIONS.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  className={`iod-v2-pill${waitTime === option.value ? " iod-v2-pill--active" : ""}`}
                  onClick={() => { setWaitTime(option.value); setShowCustomInput(false); setCustomMinutes(""); }}
                >
                  {waitOptionLabel(option)}
                </button>
              ))}
              {showCustomInput ? (
                <div className="iod-v2-custom-wrap">
                  <input
                    type="text"
                    inputMode="numeric"
                    className="iod-v2-custom-input"
                    value={customMinutes}
                    onChange={handleCustomMinutesChange}
                    placeholder="1–60"
                    autoFocus
                    maxLength={2}
                  />
                  <span className="iod-v2-custom-unit">{t("immediateOnDemand.minUnit")}</span>
                </div>
              ) : (
                <button
                  type="button"
                  className={`iod-v2-pill${isCustomWaitTime ? " iod-v2-pill--active" : ""}`}
                  onClick={() => { setShowCustomInput(true); setWaitTime(""); setCustomMinutes(""); }}
                >
                  {isCustomWaitTime ? t("immediateOnDemand.customMinutes", { count: Number(waitTime) }) : t("immediateOnDemand.other")}
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Stay near device row */}
        <div className="iod-v2-row iod-v2-row--static">
          <div className="iod-v2-row-icon">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="5" y="2" width="14" height="20" rx="2" ry="2" />
              <line x1="12" y1="18" x2="12.01" y2="18" />
            </svg>
          </div>
          <div>
            <p className="iod-v2-row-title">{t("immediateOnDemand.stayNearDevice")}</p>
            <p className="iod-v2-row-sub">{t("immediateOnDemand.chimeOnMatch")}</p>
          </div>
        </div>

        {/* When you match box */}
        <div className="iod-v2-match-box">
          <div className="iod-v2-match-box-header">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
              <path d="M13.73 21a2 2 0 0 1-3.46 0" />
            </svg>
            <span>{t("immediateOnDemand.whenYouMatch")}</span>
          </div>
          <p>{t("immediateOnDemand.whenYouMatchText")}</p>
          <button
            type="button"
            className={`iod-v2-sound-btn${isTestSoundPlaying ? " is-playing" : ""}`}
            onClick={toggleTestSound}
            aria-pressed={isTestSoundPlaying}
          >
            {isTestSoundPlaying ? (
              <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" stroke="none">
                <rect x="6" y="6" width="12" height="12" rx="2" />
              </svg>
            ) : (
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
                <path d="M19.07 4.93a10 10 0 0 1 0 14.14" />
                <path d="M15.54 8.46a5 5 0 0 1 0 7.07" />
              </svg>
            )}
            {isTestSoundPlaying ? t("immediateOnDemand.stopSound") : t("immediateOnDemand.testSound")}
          </button>
        </div>

        {/* Eligibility message */}
        {isBlockedByUpcomingAppointment && eligibility.message ? (
          <p className="immediate-ondemand-note">{displayEligibilityMessage(eligibility.message)}</p>
        ) : null}

        {/* CTA */}
        <button
          type="button"
          className="iod-v2-cta"
          onClick={goToWaitingRoom}
          disabled={!signedIntoZoom || !waitTime || !eligibility.checked || isBlockedByUpcomingAppointment}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
          </svg>
          {t("immediateOnDemand.goOnDemand")}
        </button>

        {/* Footer */}
        <p className="iod-v2-footer">
          {t("immediateOnDemand.footer")}{" "}
          <button
            type="button"
            className="iod-v2-cancel-link"
            onClick={() => navigate("/appointments")}
          >
            {t("immediateOnDemand.cancel")}
          </button>
        </p>
      </div>
    </div>
  );
}
